import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useCommentActions } from 'redux/actions/Comments'

import { TChartClick } from 'components_new/molecules/charts/types'
import { TCommentThreadData } from 'types/GlobalComment'
import { isCustomEconomyMetric, TMetricSettings } from 'types/GlobalMetric'
import { PeriodFilter } from 'types/GlobalWidget'

import { useTranslation } from 'language/translator'
import { useCommentThreads } from 'redux/hooks/Comments'
import { useKeyMetric } from 'redux/hooks/KeyMetrics'
import { useMetric } from 'redux/hooks/Metrics'
import { useTasksFromKeyMetric } from 'redux/hooks/Tasks'
import { useIsMobile, useParams } from 'utils/hooks'
import { useQueryParamState } from 'utils/queryHandler'
import { useMetricQueryParams } from 'utils/useMetricQueryParams'

import Chip from 'components_new/atoms/Chip'

import ContentBox from 'components_new/molecules/ContentBox'
import SimpleTable from 'components_new/molecules/SimpleTable'

import KeyMetricSidebar from 'components_new/organisms/KeyMetricSidebar'
import MetricQueryParamsFooter from 'components_new/organisms/MetricQueryParamsFooter'
import MetricView from 'components_new/organisms/MetricView'

import KeyMetricTemplate from 'components_new/templates/KeyMetricTemplate'

const DEFAULT_SETTINGS: TMetricSettings = {
  distribution: null,
  rolling_sum: null,
  slices: [],
  variant_kpi_template_id: null
}

const KeyMetric = () => {
  const { id } = useParams<{ id: string }>()
  const keyMetric = useKeyMetric(id)
  const { lang, t } = useTranslation()
  const { tryGetAllComments } = useCommentActions()
  const isMobile = useIsMobile()

  const [rightSidebarCollapsed, setRightSidebarCollapsed] =
    useState<boolean>(isMobile)
  const [newCommentThread, setNewCommentThread] =
    useState<TCommentThreadData | null>(null)
  const [focusedCommentThreadId, setFocusedCommentThreadId] = useState<
    string | null
  >(null)

  // query states
  const [chart, setChart] = useQueryParamState(
    'chart',
    'line',
    Object.values(['bar', 'line'])
  )
  const [queryParams, setQueryParams] = useMetricQueryParams()

  const settings: TMetricSettings =
    keyMetric?.metric.settings ?? DEFAULT_SETTINGS

  const metric = useMetric(
    keyMetric?.metric.id,
    settings.variant_kpi_template_id
  )
  const commentThreads = useCommentThreads(keyMetric?.id)

  useEffect(() => {
    if (keyMetric?.id) {
      tryGetAllComments(keyMetric.id)
    }
  }, [keyMetric?.id])

  const effectiveQueryParams = useMemo(
    () => ({
      ...queryParams,
      period_filter:
        queryParams.period_filter ?? PeriodFilter.LAST_12_MONTHS_TO_TODAY
    }),
    [queryParams]
  )

  // Strip the default period back out before writing to URL so it never
  // appears as an explicit chip (e.g. after a segment drill-down).
  const setEffectiveQueryParams = useCallback(
    (params: Parameters<typeof setQueryParams>[0]) => {
      setQueryParams(
        params.period_filter === PeriodFilter.LAST_12_MONTHS_TO_TODAY
          ? { ...params, period_filter: undefined }
          : params
      )
    },
    [setQueryParams]
  )

  const selectedChart = queryParams.segment_by ? 'bar' : chart

  // open comments
  const needsDelay = rightSidebarCollapsed

  // add comment
  const handleAddComment = (value: TChartClick) => {
    const existing = commentThreads.find(
      (thread) => thread.data.label === value.label
    )

    if (existing) {
      handleClickComment(existing.id)

      return
    }

    setRightSidebarCollapsed(false)

    if (needsDelay) {
      setTimeout(() => {
        setFocusedCommentThreadId(null)
        setNewCommentThread({
          datasetLabel: value.datasetLabel ?? keyMetric?.title ?? '',
          label: value.label,
          value: value.value ?? 0
        })
      }, 300)
    } else {
      setFocusedCommentThreadId(null)
      setNewCommentThread({
        datasetLabel: value.datasetLabel ?? keyMetric?.title ?? '',
        label: value.label,
        value: value.value ?? 0
      })
    }
  }

  // click comment
  const handleClickComment = (commentId: string) => {
    setRightSidebarCollapsed(false)

    setNewCommentThread(null)

    if (needsDelay) {
      setTimeout(() => setFocusedCommentThreadId(commentId), 300)
    } else {
      setFocusedCommentThreadId(commentId)
    }
  }

  const { tasks } = useTasksFromKeyMetric(keyMetric?.id)

  if (!keyMetric) return null

  return (
    <KeyMetricTemplate
      footer={{
        content: (
          <MetricQueryParamsFooter
            chart={selectedChart}
            id={keyMetric.metric.id}
            onChangeChart={setChart}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            settings={keyMetric.metric.settings}
          />
        ),
        height: 56
      }}
      rightSidebar={{
        collapsed: rightSidebarCollapsed,
        setCollapsed: setRightSidebarCollapsed,
        content: (
          <>
            <ContentBox
              collapse={{
                active: true,
                defaultCollapsed: true
              }}
              color="default"
              header={{ icon: { name: 'Info' }, title: 'Om' }}
            >
              <SimpleTable
                rows={[
                  { cells: [t('global', 'name'), keyMetric.title] },
                  {
                    cells: [
                      t('metrics', 'metric'),
                      isCustomEconomyMetric(metric)
                        ? metric?.full_title[lang]
                        : metric?.title[lang]
                    ]
                  },
                  {
                    cells: [
                      t('entities', 'slices'),
                      keyMetric.metric.settings?.slices.map((slice) => (
                        <Chip key={slice} label={slice} size="small" />
                      ))
                    ]
                  },
                  {
                    cells: [
                      t('metrics', 'rolling_sum'),
                      keyMetric.metric.settings?.rolling_sum
                    ]
                  },
                  {
                    cells: [
                      t('metrics', 'distribution'),
                      keyMetric.metric.settings?.distribution
                    ]
                  }
                ]}
              />
            </ContentBox>

            <KeyMetricSidebar
              focusedCommentThreadId={focusedCommentThreadId}
              newCommentThread={newCommentThread}
              id={id}
              setFocusedCommentThreadId={setFocusedCommentThreadId}
              setNewCommentThread={setNewCommentThread}
            />
          </>
        ),
        size: 'medium'
      }}
      variant="static"
    >
      <MetricView
        chart={selectedChart}
        focusedMarkerId={focusedCommentThreadId ?? undefined}
        keyMetric={keyMetric}
        onAddComment={handleAddComment}
        onClickComment={handleClickComment}
        queryParams={effectiveQueryParams}
        setQueryParams={setEffectiveQueryParams}
        tasks={tasks}
      />
    </KeyMetricTemplate>
  )
}

export default KeyMetric
